

import { useLocationPermissionSync } from "../../../utils/getPositionWithFallback";
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { FormControl, Select, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import BaseCard from "../../../components/Cards/Card";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import crossmap from "../../../assets/crossmap.svg";
import { useSelector } from "react-redux";
import LocationPermissionModal from "../../../components/PermissionModal";
import { setProfileDraft } from "./ProfileDraft";
import { ClickAwayListener } from "@mui/material";
import { Country, State, City } from "country-state-city";
import { GoogleMap, Marker, Circle } from "@react-google-maps/api";
import SearchableDropdown from "../../../components/SearchableDropdown";
const defaultCenter = { lat: 37.0902, lng: -95.7129 };
const circularRadius = 100;
const mapContainerStyle = { width: "100%", height: "100%" };

const iso2ToContinent = (iso2) => {
  const m = (iso2 || "").toUpperCase();
  const NA = ["US", "CA", "MX"];
  const SA = ["BR", "AR", "CL", "CO", "PE", "UY", "PY", "BO", "VE", "EC"];
  const EU = ["GB", "FR", "DE", "ES", "IT", "NL", "SE", "NO", "FI", "PL", "PT", "IE", "AT", "GR", "CZ"];
  const AS = ["PK", "IN", "CN", "JP", "AE", "SA", "SG", "TH", "ID", "MY", "BD", "PH", "KR"];
  const AF = ["ZA", "NG", "EG", "KE", "MA", "TZ", "UG", "DZ"];
  const OC = ["AU", "NZ", "FJ", "PG"];
  if (NA.includes(m)) return "North America";
  if (SA.includes(m)) return "South America";
  if (EU.includes(m)) return "Europe";
  if (AS.includes(m)) return "Asia";
  if (AF.includes(m)) return "Africa";
  if (OC.includes(m)) return "Oceania";
  return "Unknown";
};

const Location = () => {
  const [searchBarOpen, setSearchBarOpen] = useState(false);
  const [position, setPosition] = useState(defaultCenter);
  const [address, setAddress] = useState("Fetching address...");
  const [displayAddress, setDisplayAddress] = useState("Fetching address...");
  const [showFullMap, setShowFullMap] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [locating, setLocating] = useState(false);
  const locationRequestRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState("");
const [isContinueClicked, setIsContinueClicked] = useState(false);
const [suggestions, setSuggestions] = useState([]);
const [isSelecting, setIsSelecting] = useState(false); 
const [selectedCountryIso, setSelectedCountryIso] = useState("");
const [selectedStateIso, setSelectedStateIso] = useState("");
const [regions, setRegions] = useState([]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addressRef = useRef(null);
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const fullMapRef = useRef(null);
  const isOnline = () => navigator.onLine;
 

  
useEffect(() => {
  if (isSelecting) return; // stop re-fetching after a selection

  const query = searchQuery.trim();
  if (!query) {
    setSuggestions([]);
    return;
  }

  if (!window.google?.maps?.places) {
    setSuggestions([]);
    return;
  }

  const timeout = setTimeout(() => {
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input: query }, (predictions, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        predictions
      ) {
        setSuggestions(predictions);
      } else {
        setSuggestions([]);
      }
    });
  }, 300);

  return () => clearTimeout(timeout);
}, [searchQuery, isSelecting]);

const WORLD_REGIONS = [
  "Africa",
  "Asia",
  "Europe",
  "North America",
  "South America",
  "Oceania",
  "Antarctica",
];
const [selectedRegion, setSelectedRegion] = useState("");
const [countries, setCountries] = useState([]);

const SearchableSelect = ({
  value,
  options,
  disabled,
  onChange,
  placeholder = "Select Option",
}) => {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef(null);

  const filtered = options.filter((item) =>
    item.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <FormControl
      fullWidth
      size="small"
      sx={{
        "& .MuiOutlinedInput-root": {
          height: 44,
          borderRadius: "10px",
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
          "& fieldset": { borderColor: "#DFE1E6", borderWidth: "2px" },
          "&:hover fieldset": { borderColor: disabled ? "#ccc" : "#1976d2" },
          "&.Mui-focused fieldset": { borderColor: "#1976d2" },
        },
      }}
    >
      <Select
        value={isOpen ? search : value}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false);
          setSearch("");
        }}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        renderValue={(val) =>
          val ? val : <span style={{ color: "#6B778C" }}>{placeholder}</span>
        }
        MenuProps={{
          keepMounted: true,
          disablePortal: true,
          PaperProps: {
            onMouseDown: (e) => e.stopPropagation(),
            sx: { maxHeight: 350, paddingTop: 0 },
          },
        }}
      >

        {options.map((item) => (
          <MenuItem
            key={item}
            value={item}
            sx={{
              fontSize: "14px",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {item}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

  const profileUpdateTokenFromRedux = useSelector((s) => s.auth.profileUpdateToken);
  const profileUpdateToken =
    profileUpdateTokenFromRedux || localStorage.getItem("profileUpdateToken");

  const [locationFields, setLocationFields] = useState({
    latitude: defaultCenter.lat,
    longitude: defaultCenter.lng,
    address: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
    countryCode: "",
    region: "",
    continent: "",
    timezone: "",
  });

useEffect(() => {
  if (!selectedRegion) return;

  const loadCountriesByRegion = async () => {
    try {
      setLoadingCountries(true);
      const res = await fetch(
        `https://restcountries.com/v3.1/region/${selectedRegion.toLowerCase()}`
      );
      const json = await res.json();

      const list = json.map((c) => c.name.common).sort();
      setCountries(list);
    } catch (err) {
      toast.error("Failed to load countries");
    } finally {
      setLoadingCountries(false);
    }
  };

  loadCountriesByRegion();
}, [selectedRegion]);

// Load all countries and their states from countriesnow API
useEffect(() => {
  const loadCountriesAndStates = async () => {
    try {
      const res = await fetch(
        "https://countriesnow.space/api/v0.1/countries/states"
      );
      const json = await res.json();

      if (json?.data) {
        setCountriesData(json.data); // THIS WAS MISSING
      }
    } catch (err) {
      // toast.error("Failed to load country/state data");
    }
  };

  loadCountriesAndStates();
}, []);
const fetchAddress = ({ lat, lng }) => {
   if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection");
      setLocating(false);
      return;
    }

  if (!window.google?.maps) {
    setLocating(false);
    return;
  }

  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: { lat, lng } }, (results, status) => {
    setLocating(false);
    if (status !== "OK" || !results?.[0]) {
      setAddress("Address not found");
      setDisplayAddress("Address not found");
      if (addressRef.current) addressRef.current.value = "Address not found";
      return;
    }

    const result = results[0];
    const formatted = result.formatted_address || "Address not found";
    const addressComponents = result.address_components || [];

    const getComponent = (types, key = "long_name") =>
      addressComponents.find((component) =>
        types.every((type) => component.types.includes(type))
      )?.[key] || "";

    const streetNumber = getComponent(["street_number"]);
    const route = getComponent(["route"]);
    const neighborhood =
      getComponent(["neighborhood"]) ||
      getComponent(["sublocality", "sublocality_level_1"]) ||
      getComponent(["sublocality"]) ||
      getComponent(["premise"]);
    const streetOnly = [streetNumber, route].filter(Boolean).join(" ") || neighborhood;
    const iso2 = getComponent(["country"], "short_name").toUpperCase();
    const detectedCity =
      getComponent(["locality"]) ||
      getComponent(["postal_town"]) ||
      getComponent(["administrative_area_level_2"]) ||
      getComponent(["sublocality", "sublocality_level_1"]) ||
      neighborhood;

    const province =
      getComponent(["administrative_area_level_1"]) ||
      getComponent(["administrative_area_level_2"]);

    setAddress(formatted);
    setDisplayAddress(formatted);
    if (addressRef.current) addressRef.current.value = formatted;

    setLocationFields({
      latitude: lat,
      longitude: lng,
      address: formatted,
      street: streetOnly,
      city: detectedCity,
      province,
      postalCode: getComponent(["postal_code"]),
      country: getComponent(["country"]),
      countryCode: iso2,
      region: iso2ToContinent(iso2),
      continent: iso2ToContinent(iso2),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    });

    setSelectedCountry(getComponent(["country"]));
    setSelectedState(province);
    setSelectedCity(detectedCity || "");
  });
};



  const updatePosition = (coords) => {
    setPosition(coords);
    fetchAddress(coords);
  };

  const handleDragEnd = (e) => {
    const coords = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    updatePosition(coords);
  };


  // Single geolocation request used by every entry point (mount, online-retry,
  // modal). The permission modal opens ONLY when the user has actually denied
  // access; timeouts / unavailable fixes retry with different settings instead.
  const getCurrentLocation = () => {
  if (!navigator.geolocation) {
    toast.error("Geolocation not supported");
    return;
  }

   if (!isOnline()) {
      toast.error(t("locationInternetError") || "No internet connection. Please check your connection.");
      return;
    }

  const requestId = ++locationRequestRef.current;
  const isStale = () => requestId !== locationRequestRef.current;
  setLocating(true);

  const handleSuccess = (pos) => {
    if (isStale()) return;
    setShowPermissionModal(false);
    updatePosition({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
    });
  };

  const handleFinalError = (error) => {
    if (isStale()) return;
    setLocating(false);
    if (error.code === error.PERMISSION_DENIED) {
      setShowPermissionModal(true);
    } else {
      setAddress("Unable to fetch location");
      setDisplayAddress("Unable to fetch location");
      toast.error("Unable to fetch your location. Please make sure Location Services are on and try again.");
    }
  };

  // 1st attempt: fast network/Wi-Fi based fix (accepts a recent cached one).
  navigator.geolocation.getCurrentPosition(
    handleSuccess,
    (error) => {
      if (isStale()) return;
      if (error.code === error.PERMISSION_DENIED) {
        handleFinalError(error);
        return;
      }
      // 2nd attempt: GPS, given plenty of time (iOS can be slow to get a fix).
      navigator.geolocation.getCurrentPosition(handleSuccess, handleFinalError, {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0,
      });
    },
    {
      enableHighAccuracy: false,
      timeout: 15000,
      maximumAge: 60000,
    }
  );
};


  useEffect(() => {
    getCurrentLocation();
  }, []);

  useEffect(() => {
    if (position && mapRef.current) {
      mapRef.current.panTo(position);
    }
    if (position && fullMapRef.current) {
      fullMapRef.current.panTo(position);
    }
  }, [position]);

  // Manual address toggle now stays on this page
  const handleManualToggle = (e) => {
    const checked = e.target.checked;
    setManualAddress(checked);
  };

  // Continue without backend save; persist to draft and navigate
  const handleContinue = async () => {
    setIsContinueClicked(true);
    setSaving(true);
    setProfileDraft({
      country: locationFields.country,
      community: "",
      province: locationFields.province,
      city: locationFields.city,
      postal_code: locationFields.postalCode,
      street_address: locationFields.street || "",
      region_code: locationFields.countryCode,
    });
    setTimeout(() => {
      navigate("/region");
      setSaving(false);
    }, 500);
  };

  const handleSearch = async () => {
    if (!searchQuery || !window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: searchQuery }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const location = results[0].geometry.location;
        updatePosition({ lat: location.lat(), lng: location.lng() });
      } else {
        toast.error("No results found");
      }
    });
  };

  // Country/State/City cascading dropdowns data
  const [countriesData, setCountriesData] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
useEffect(() => {
  const list = Country.getAllCountries().map((c) => ({
    name: c.name,
    isoCode: c.isoCode,
  }));

  setCountriesData(list);
  setCountries(list.map((c) => c.name));
}, []);

const handleRegionChange = (value) => {
  setSelectedRegion(value);
  setSelectedCountry("");
  setSelectedState("");
  setSelectedCity("");
  setStates([]);
  setCities([]);
  setLoadingCountries(true);
  fetchCountriesByRegion(value);
};

// Fetch countries based on the selected region
const fetchCountriesByRegion = async (region) => {
  try {
    const res = await fetch(`https://restcountries.com/v3.1/region/${region.toLowerCase()}`);
    const data = await res.json();
    const countryList = data.map((country) => country.name.common).sort();
    setCountries(countryList);
  } catch (err) {
    toast.error("Failed to load countries");
  } finally {
    setLoadingCountries(false);
  }
};
const handleCountryChange = (value) => {
  setSelectedCountry(value);
  setSelectedState("");
  setSelectedCity("");
  setStates([]);
  setCities([]);
  setLoadingStates(true);

  const countryObj = Country.getAllCountries().find((country) => country.name === value);
  if (countryObj) {
    setSelectedCountryIso(countryObj.isoCode);

    // ✅ Store the full state objects, not just names
    const statesList = State.getStatesOfCountry(countryObj.isoCode);
    setStates(statesList); // Keep full objects with isoCode
  }

  setLoadingStates(false);
};

const handleStateChange = (value) => {
  setSelectedState(value);
  setSelectedCity("");
  setCities([]);
  setLoadingCities(true);

  // ✅ Now this will work because states contains objects
  const stateObj = states.find((state) => state.name === value);

  if (stateObj) {
    setSelectedStateIso(stateObj.isoCode);

    // Fetch cities for the selected state
    const cityList = City.getCitiesOfState(selectedCountryIso, stateObj.isoCode)
      .map((city) => city.name)
      .sort();

    setCities(cityList);
  }

  setLoadingCities(false);
};


const handleCityChange = (value) => {
  setSelectedCity(value);
};



  // Reflect browser location allow/block changes live (no refresh needed).
  useLocationPermissionSync({
    onGranted: () => {
      if (showPermissionModal) {
        setShowPermissionModal(false);
        getCurrentLocation();
      }
    },
    onDenied: () => setShowPermissionModal(true),
  });
useEffect(() => {
  const handleOnline = () => {
    toast.success(t("internetRestored"));
    // Retry fetching address if coordinates exist
    if (position?.lat && position?.lng) {
      fetchAddress(position);
    }
  };

  const handleOffline = () => {
    toast.error(t("internetConnectionLost"));
  };

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}, [position]);

const sortedRegions = [...WORLD_REGIONS].sort();

const isManualAddressInvalid =
  manualAddress &&
  (
    !selectedCountry ||
    !selectedState ||
    !selectedCity
  );
  const LOCATION_LOCKED = true;
  return (
      <Box sx={{ py: isMobile ? 5 : 8, px: 2 }}>
        <BaseCard maxWidth={isMobile ? "100%" : "70%"}>
          <CustomText customStyle={{ ...textStyles.h4, color: theme.palette.tertiary.main }}>
            {t("pinLocationTitle")}
          </CustomText>
          <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary, pt: 0.7 }}>
            {t("pinLocationSubtitle")}
          </CustomText>

   
             
      <Box sx={{ mt: 1, mb: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap:2, }}>
            <input
              type="checkbox"
              id="manualAddress"
              checked={manualAddress}
              onChange={handleManualToggle}
              style={{ width: 18, height: 18 }}
            />
            <CustomText customStyle={{ ...textStyles.body4, color: theme.palette.text.primary }}>
              {t("manualAddress")}
            </CustomText>
          </Box>
        </Box>
          {/* Actions - only when not manual address */}
          {!manualAddress && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 2 : 0,
              justifyContent: "space-between",
              alignItems: "center",
              my: 2,
            }}
          >
            <CustomText customStyle={{ ...textStyles.body4, color: theme.palette.text.primary }}>
              {t("pickFromLive")} <span style={{ color: "red" }}>*</span>
            </CustomText>
            <Box sx={{ display: "flex", gap: 1 }}>
          
              {/* <IconButton
                onClick={handleGeolocate}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  borderRadius: 1,
                  width: 40,
                  height: 31,
                  px: 2,
                  "&:hover": { backgroundColor: "#1976d2" },
                }}
              >
                <RoomIcon fontSize="small" />
              </IconButton> */}
              <CustomButton
                label={t("fullMap")}
                size="small"
                sx={{
                  height: 31,
                  minWidth: "unset",
                  px: 2,
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  fontSize: 12,
                  "&:hover": { backgroundColor: "#115293" },
                }}
                onClick={() => setShowFullMap(true)}
              />
            </Box>
          </Box>
          )}

    

          {!manualAddress && searchBarOpen && (
            <ClickAwayListener
  onClickAway={() => {
    setSuggestions([]);
    setIsSelecting(false);
  }}
>
  <Box sx={{ mb: 2, position: "relative" }}>
    <input
      type="text"
      placeholder={t("searchLocation")}
      value={searchQuery}
      onChange={(e) => {
        setSearchQuery(e.target.value);
        setIsSelecting(false);
      }}
      onFocus={() => {
        if (searchQuery.trim()) setIsSelecting(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          handleSearch();
        }
      }}
      style={{
        width: "100%",
        padding: "10px 14px",
        fontSize: "15px",
        border: "1px solid #ccc",
        borderRadius: "8px",
        marginBottom: "12px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        fontFamily: "Poppins, sans-serif",
      }}
    />

    {suggestions.length > 0 && (
      <Box
        sx={{
          position: "absolute",
          zIndex: 1000,
          width: "100%",
          background: "#fff",
          border: "1px solid #ccc",
          borderRadius: "8px",
          maxHeight: "200px",
          overflowY: "auto",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}
      >
        {suggestions.map((sug, idx) => (
          <Box
            key={idx}
            onClick={() => {
              if (!window.google?.maps) return;

              const geocoder = new window.google.maps.Geocoder();
              setIsSelecting(true);
              geocoder.geocode({ placeId: sug.place_id }, (results, status) => {
                if (status === "OK" && results?.[0]) {
                  const location = results[0].geometry.location;
                  updatePosition({ lat: location.lat(), lng: location.lng() });
                  setSearchQuery(results[0].formatted_address);
                }

                setSuggestions([]);
                document.activeElement.blur();
                requestAnimationFrame(() => setIsSelecting(false));
              });
            }}
            sx={{
              p: 1,
              cursor: "pointer",
              "&:hover": { backgroundColor: "#f5f5f5" },
              fontSize: 14,
              fontFamily: "Poppins, sans-serif",
            }}
          >
            {sug.description}
          </Box>
        ))}
      </Box>
    )}
  </Box>
</ClickAwayListener>

          )}


          {/* 🗺️ Map */}
          {!manualAddress && (
            <>
          <Box sx={{ position: "relative", height: 300, borderRadius: 2, overflow: "hidden" }}>
            {window.google?.maps ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={position}
                zoom={15}
                onLoad={(map) => {
                  mapRef.current = map;
                }}
                onUnmount={() => {
                  mapRef.current = null;
                }}
                options={{
                  fullscreenControl: false,
                  mapTypeControl: false,
                  streetViewControl: false,
                }}
              >
                <Marker position={position} draggable={false} onDragEnd={handleDragEnd} />
                <Circle
                  center={position}
                  radius={circularRadius}
                  options={{
                    fillColor: "#3f51b5",
                    fillOpacity: 0.2,
                    strokeColor: "#3f51b5",
                    strokeOpacity: 1,
                    strokeWeight: 1,
                  }}
                />
              </GoogleMap>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {locating && (
              <Box
                sx={{
                  position: "absolute",
                  top: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.8,
                  borderRadius: "20px",
                  backgroundColor: "rgba(255,255,255,0.95)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              >
                <CircularProgress size={16} />
                <span style={{ fontSize: 13, fontFamily: "Poppins, sans-serif", color: "#2C384C" }}>
                  {t("fetchingLocation") || "Fetching your location..."}
                </span>
              </Box>
            )}
          </Box>
      
           <textarea
        ref={addressRef}
        type="text"
        
             readOnly={!manualAddress}
          value={manualAddress ? displayAddress : address}
          onChange={manualAddress ? (e) => setDisplayAddress(e.target.value) : undefined}
          placeholder={manualAddress ? "Enter your address manually" : "Address will be detected from map"}
        style={{
             width: "100%",
    padding: "12px",
    fontSize: "16px",
    border: "1px solid #ccc",
    borderRadius: "8px",
    marginTop: "10px",
    backgroundColor: "#f4f4f4",
    fontFamily: "Poppins, sans-serif",
    resize: "vertical",      // allow manual resize if needed
    overflowY: "auto",       // show scroll when text exceeds height
    lineHeight: "1.4",
        }}
      />
        </>
          )}
      




      
{manualAddress && (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2, mt: 2 }}>

  
    <Box>
      <CustomText
        customStyle={{
          ...textStyles.body5,
          color: "#6B778C",
          mb: 1,
          letterSpacing: "0.3px",
          textTransform: "capitalize",
          textAlign:"left"
        }}
      >
        {t("country")} <span style={{ color: "red" }}>*</span>
      </CustomText>

    <SearchableDropdown
  value={selectedCountry}
  options={countries}
  onChange={handleCountryChange}
  placeholder={t("selectCountry")}
   disabled={LOCATION_LOCKED}
/>
    </Box>

    {/* PROVINCE */}
    <Box>
      <CustomText
        customStyle={{
          ...textStyles.body5,
          color: "#6B778C",
          mb: 1,
          letterSpacing: "0.3px",
          textTransform: "capitalize",
            textAlign:"left"
        }}
      >
        {t("province")} <span style={{ color: "red" }}>*</span>
      </CustomText>
<SearchableDropdown
  value={selectedState}
  options={states.map(s => s.name)} // ✅ Extract just the names for display
  onChange={handleStateChange}
  placeholder={t("selectProvince")}
  // disabled={!selectedCountry}
   disabled={LOCATION_LOCKED}
/>
    
    </Box>

    {/* CITY */}
    <Box>
      <CustomText
        customStyle={{
          ...textStyles.body5,
          color: "#6B778C",
          mb: 1,
          letterSpacing: "0.3px",
          textTransform: "capitalize",
            textAlign:"left"
        }}
      >
        {t("city")} <span style={{ color: "red" }}>*</span>
      </CustomText>

  <SearchableDropdown
  value={selectedCity}
  options={cities}
  onChange={handleCityChange}
  placeholder={t("selectCity")}
  // disabled={!selectedState}
   disabled={LOCATION_LOCKED}
/>
    </Box>

  </Box>
)}


        {/* Footer */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mt: 2, gap: 2 }}>
          <CustomButton
            label={t("back")}
            onClick={() => navigate(-1)}
             disabled={isContinueClicked} 
            size="medium"
            sx={{
              fontSize: "16px",
              px: 4,
               backgroundColor: isContinueClicked ? "#f0f0f0" : "white",
    color: isContinueClicked ? "#999" : "black",
    "&:hover": {
      backgroundColor: isContinueClicked ? "#f0f0f0" : "white",
      color: isContinueClicked ? "#999" : "black",
    },
            }}
          />

          <CustomButton
            onClick={handleContinue}
  disabled={saving || isManualAddressInvalid}
            label={
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                {saving && <CircularProgress size={18} sx={{ color: "#fff" }} />}
                {t("continue")}
              </Box>
            }
            sx={{ py: 1, px: 6 }}
          />
        </Box>
        </BaseCard>

        {/* Full-screen Map */}
        {showFullMap && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "#fff",
              zIndex: 1300,
              p: 2,
            }}
          >
            <IconButton onClick={() => setShowFullMap(false)} sx={{ position: "absolute", top: 16, right: 16, zIndex: 1400 }}>
              <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
            </IconButton>
            {window.google?.maps ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={position}
                zoom={15}
                onLoad={(map) => {
                  fullMapRef.current = map;
                }}
                onUnmount={() => {
                  fullMapRef.current = null;
                }}
                options={{
                  fullscreenControl: false,
                  mapTypeControl: false,
                  streetViewControl: false,
                }}
              >
                <Marker position={position} draggable={false} onDragEnd={handleDragEnd} />
                <Circle
                  center={position}
                  radius={100}
                  options={{
                    fillColor: "#3f51b5",
                    fillOpacity: 0.2,
                    strokeColor: "#3f51b5",
                    strokeOpacity: 1,
                    strokeWeight: 1,
                  }}
                />
              </GoogleMap>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            {locating && (
              <Box
                sx={{
                  position: "absolute",
                  top: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.8,
                  borderRadius: "20px",
                  backgroundColor: "rgba(255,255,255,0.95)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  pointerEvents: "none",
                  zIndex: 1400,
                }}
              >
                <CircularProgress size={16} />
                <span style={{ fontSize: 13, fontFamily: "Poppins, sans-serif", color: "#2C384C" }}>
                  {t("fetchingLocation") || "Fetching your location..."}
                </span>
              </Box>
            )}
          </Box>
        )}

        {/* Permission Modal */}
        <LocationPermissionModal
          open={showPermissionModal}
          onAllow={() => {
            setShowPermissionModal(false);
            getCurrentLocation();
          }}
          onCancel={() => setShowPermissionModal(false)}
        />
      </Box>
  );
};

export default Location;

















